import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DropdownMenu from './DropdownMenu';
import TimingAnimation from './TimingAnimation';
import { useTheme } from './ThemeContext';

const TutorialScreen = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const s = isDark ? darkStyles : lightStyles;

  const tutorialSteps = [
    {
      title: "Welcome to Hititatzero!",
      content: (
        <div style={s.stepContent}>
          <p style={s.description}>
            This is a game about precision and timing. Your goal is to click a button every second as precisely as possible!
          </p>
          <div style={s.infoBox}>
            <p style={s.infoTitle}>Key Points:</p>
            <ul style={s.list}>
              <li>Click the button at regular 1-second intervals</li>
              <li>Try to be as precise as possible</li>
              <li>Compare your results with other players</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Game Modes",
      content: (
        <div style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px', margin: '0 auto' }}>
            <div style={{
              padding: '1.5rem',
              borderRadius: '8px',
              color: 'white',
              background: isDark
                ? 'linear-gradient(45deg, rgba(0, 80, 40, 0.85), rgba(0, 100, 50, 0.85))'
                : 'linear-gradient(45deg, #66bb6a, #43a047)',
              border: isDark ? '1px solid rgba(0, 245, 196, 0.35)' : 'none',
              boxShadow: isDark ? '0 0 14px rgba(0, 245, 196, 0.15)' : 'none',
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Practice Mode</h3>
              <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Play solo to practice your timing</p>
              <p style={{ fontSize: '0.875rem', opacity: '0.9' }}>Perfect for learning!</p>
            </div>

            <div style={{
              padding: '1.5rem',
              borderRadius: '8px',
              color: 'white',
              background: isDark
                ? 'linear-gradient(45deg, rgba(80, 40, 0, 0.85), rgba(100, 50, 0, 0.85))'
                : 'linear-gradient(45deg, #ffa726, #fb8c00)',
              border: isDark ? '1px solid rgba(255, 165, 0, 0.35)' : 'none',
              boxShadow: isDark ? '0 0 14px rgba(255, 130, 0, 0.15)' : 'none',
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Multiplayer Mode</h3>
              <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Compete with other players</p>
              <p style={{ fontSize: '0.875rem', opacity: '0.9' }}>Join or create rooms!</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Time Horizons",
      content: (
        <div style={s.stepContent}>
          <p style={s.description}>Choose your challenge level:</p>
          <div style={s.horizonsContainer}>
            {[10, 20, 30].map((seconds) => (
              <div key={seconds} style={s.horizonBox}>
                <h3 style={s.horizonTitle}>{seconds}s</h3>
                <p style={s.horizonDescription}>
                  {seconds === 10 ? 'Beginner' : seconds === 20 ? 'Medium' : 'Expert'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      title: "Understanding Average Difference",
      content: (
        <div style={s.stepContent}>
          <p style={s.description}>
            The Average Difference shows how far your clicks are from the perfect 1-second interval on average.
          </p>
          <svg style={s.timeline} viewBox="0 35 400 200">
            <defs>
              <marker id="startArrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#43a047" />
              </marker>
              <marker id="endArrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#43a047" />
              </marker>
            </defs>

            <line x1="50" y1="50" x2="350" y2="50" stroke={isDark ? 'rgba(0,245,255,0.2)' : '#ddd'} strokeWidth="2" />
            <circle cx="100" cy="50" r="5" fill="#43a047" />
            <circle cx="200" cy="50" r="5" fill="#43a047" />
            <circle cx="300" cy="50" r="5" fill="#43a047" />
            <TimingAnimation
              startX={50} xOffset={20} y={175} arrowY={35}
              strokeColor={isDark ? 'rgba(0,245,255,0.25)' : '#666'}
              textColor="#43a047" rectColor="#43a047" arrowColor="#43a047"
              firstArrowStart1={105} firstArrowEnd1={195} secondArrowStart2={205} secondArrowEnd2={295}
              firstArrowStart="0.5s" firstArrowDuration="2s" secondArrowStart="3s" secondArrowDuration="2s"
              firstEllipseStart="1s" secondEllipseStart="3.5s" lineStart="4s" rectStart="4.5s"
              averageText="Average = 1 s = 1000 ms" arrowText="1 s" ellipsesStayVisible={false}
            />
            <TimingAnimation
              startX={240} xOffset={20} y={175} arrowY={85}
              strokeColor={isDark ? 'rgba(0,245,255,0.25)' : '#666'}
              textColor="#ff6f61" rectColor="#ff6f61" arrowColor="#ff6f61"
              firstArrowStart1={105} firstArrowEnd1={185} secondArrowStart2={195} secondArrowEnd2={265}
              firstArrowStart="5s" firstArrowDuration="2s" secondArrowStart="7.5s" secondArrowDuration="2s"
              firstEllipseStart="5.5s" secondEllipseStart="8s" lineStart="8.5s" rectStart="9s"
              averageText="Average = 0.85 s = 850 ms"
              firstEllipseText="0.9 s" secondEllipseText="0.8 s"
              firstArrowText="0.9 s" secondArrowText="0.8 s"
            />

            <line x1="50" y1="100" x2="350" y2="100" stroke={isDark ? 'rgba(0,245,255,0.2)' : '#ddd'} strokeWidth="2" />
            <circle cx="100" cy="100" r="5" fill="#ff6f61" />
            <circle cx="190" cy="100" r="5" fill="#ff6f61" />
            <circle cx="270" cy="100" r="5" fill="#ff6f61" />

            <circle cx="50" cy="135" r="5" fill="#43a047" />
            <text x="70" y="140" fill={isDark ? '#8ab4cc' : '#333'}>Perfect Timing</text>
            <circle cx="200" cy="135" r="5" fill="#ff6f61" />
            <text x="220" y="140" fill={isDark ? '#8ab4cc' : '#333'}>Your Clicks</text>
          </svg>
        </div>
      )
    },
    {
      title: "Understanding Mean Absolute Error",
      content: (
        <div style={s.stepContent}>
          <p style={s.description}>
            The Mean Absolute Error (MAE) measures how consistent your timing is, regardless of whether you're clicking too fast or too slow. <br />
            <strong>It is the metric that will rank you against other players</strong>.
          </p>
          <svg style={s.timeline} viewBox="0 35 400 200">
            <line x1="50" y1="50" x2="350" y2="50" stroke={isDark ? 'rgba(0,245,255,0.2)' : '#ddd'} strokeWidth="2" />
            <circle cx="100" cy="50" r="5" fill="#43a047" />
            <circle cx="200" cy="50" r="5" fill="#43a047" />
            <circle cx="300" cy="50" r="5" fill="#43a047" />

            <line x1="50" y1="100" x2="350" y2="100" stroke={isDark ? 'rgba(0,245,255,0.2)' : '#ddd'} strokeWidth="2" />
            <circle cx="100" cy="100" r="5" fill="#ff6f61" />
            <circle cx="190" cy="100" r="5" fill="#ff6f61" />
            <circle cx="270" cy="100" r="5" fill="#ff6f61" />

            <line x1="190" y1="120" x2="200" y2="120" stroke="#ff6f61" strokeWidth="2" />
            <text x="195" y="140" textAnchor="middle" fill="#ff6f61" fontSize="12" fontFamily="Arial, sans-serif">0.1 s</text>

            <line x1="270" y1="120" x2="290" y2="120" stroke="#ff6f61" strokeWidth="2" />
            <text x="280" y="140" textAnchor="middle" fill="#ff6f61" fontSize="12" fontFamily="Arial, sans-serif">0.2 s</text>

            <text x="240" y="80" textAnchor="middle" fill={isDark ? '#66aaff' : '#030bff'} fontSize="12" fontFamily="Arial, sans-serif">1 s</text>
            <line x1="190" y1="90" x2="290" y2="90" stroke={isDark ? '#4488dd' : '#0e37eb'} strokeWidth="2" />
            <circle cx="290" cy="100" r="5" fill={isDark ? '#4488dd' : '#030bff'} />

            <circle cx="5" cy="170" r="5" fill="#43a047" />
            <text x="20" y="175" fill={isDark ? '#8ab4cc' : '#333'}>Perfect Clicks</text>
            <circle cx="135" cy="170" r="5" fill="#ff6f61" />
            <text x="150" y="175" fill={isDark ? '#8ab4cc' : '#333'}>Your Clicks</text>
            <circle cx="245" cy="170" r="5" fill={isDark ? '#4488dd' : '#030bff'} />
            <text x="260" y="175" fill={isDark ? '#8ab4cc' : '#333'}>Your Next perfect</text>

            <rect x="20" y="200" width="350" height="30" fill="#ff6f61" rx="4" />
            <text x="195" y="220" textAnchor="middle" fill="white" fontSize="12" fontFamily="Arial, sans-serif">Mean absolute error = (0.1 s + 0.2 s)/2 = 0.15 s = 150 ms</text>

            <rect x="20" y="240" width="350" height="30" fill="#43a047" rx="4" />
            <text x="195" y="260" textAnchor="middle" fill="white" fontSize="12" fontFamily="Arial, sans-serif">Mean absolute error of perfect score = 0 s = 0 ms</text>
          </svg>
        </div>
      )
    },
    {
      title: "Tips for Success",
      content: (
        <div style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px', margin: '0 auto' }}>
            <div style={{
              padding: '1.5rem',
              borderRadius: '8px',
              backgroundColor: isDark ? 'rgba(4, 7, 18, 0.85)' : '#f5f5f5',
              border: isDark ? '1px solid rgba(0, 245, 255, 0.12)' : 'none',
              boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.75rem', color: isDark ? '#ddeeff' : '#333' }}>Finding Your Rhythm</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', color: isDark ? '#8ab4cc' : '#555' }}>
                <li>Try counting in your head</li>
                <li>Develop a consistent clicking pattern</li>
                <li>Practice with shorter intervals first</li>
              </ul>
            </div>

            <div style={{
              padding: '1.5rem',
              borderRadius: '8px',
              backgroundColor: isDark ? 'rgba(4, 7, 18, 0.85)' : '#f5f5f5',
              border: isDark ? '1px solid rgba(0, 245, 255, 0.12)' : 'none',
              boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.75rem', color: isDark ? '#ddeeff' : '#333' }}>Common Mistakes</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', color: isDark ? '#8ab4cc' : '#555' }}>
                <li>Clicking too quickly when nervous</li>
                <li>Losing focus mid-game</li>
                <li>Not maintaining a steady rhythm</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div style={s.container}>
      <DropdownMenu isSoloMode={true} />
      <div style={s.content}>
        <h1 style={s.title}>{tutorialSteps[currentStep].title}</h1>

        <div style={s.mainContent}>
          {tutorialSteps[currentStep].content}
        </div>

        <div style={s.navigation}>
          <button
            onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
            style={{
              ...s.navButton,
              ...(currentStep === 0 ? s.navButtonDisabled : s.navButtonPrimary)
            }}
            disabled={currentStep === 0}
          >
            Previous
          </button>
          <div style={s.dots}>
            {tutorialSteps.map((_, index) => (
              <div
                key={index}
                style={{ ...s.dot, ...(index === currentStep ? s.dotActive : {}) }}
              />
            ))}
          </div>
          <button
            onClick={() => {
              if (currentStep < tutorialSteps.length - 1) {
                setCurrentStep(currentStep + 1);
              } else {
                navigate('/');
              }
            }}
            style={{ ...s.navButton, ...s.navButtonPrimary }}
          >
            {currentStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

const lightStyles = {
  container: {
    minHeight: '100vh',
    background: '#f8f8f4',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  content: {
    width: '100%',
    maxWidth: '800px',
    background: 'rgba(255, 255, 255, 0.85)',
    borderRadius: '15px',
    padding: '30px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
    margin: '20px auto',
  },
  title: {
    fontSize: '2rem',
    color: '#ff6f61',
    textAlign: 'center',
    marginBottom: '30px',
  },
  mainContent: {
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepContent: {
    width: '100%',
    textAlign: 'center',
  },
  description: {
    fontSize: '1.2rem',
    color: '#333',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  infoBox: {
    background: '#fff',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  infoTitle: {
    fontSize: '1.2rem',
    color: '#ff6f61',
    marginBottom: '15px',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    textAlign: 'left',
    margin: '0 auto',
    maxWidth: '400px',
  },
  horizonsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    margin: '20px 0',
  },
  horizonBox: {
    background: 'white',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    width: '120px',
  },
  horizonTitle: {
    fontSize: '1.6rem',
    color: '#ff6f61',
    marginBottom: '5px',
  },
  horizonDescription: {
    fontSize: '0.9rem',
    color: '#666',
  },
  timeline: {
    width: '100%',
    maxWidth: '600px',
    height: '200px',
    margin: '20px auto',
  },
  navigation: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '30px',
  },
  navButton: {
    padding: '10px 20px',
    borderRadius: '25px',
    border: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  navButtonPrimary: {
    background: '#ff6f61',
    color: 'white',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  navButtonDisabled: {
    background: '#ccc',
    cursor: 'not-allowed',
    color: '#666',
  },
  dots: {
    display: 'flex',
    gap: '8px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#ddd',
  },
  dotActive: {
    background: '#ff6f61',
  },
};

const darkStyles = {
  ...lightStyles,
  container: {
    ...lightStyles.container,
    background: 'transparent',
  },
  content: {
    ...lightStyles.content,
    background: 'rgba(8, 12, 26, 0.95)',
    border: '1px solid rgba(0, 245, 255, 0.14)',
    boxShadow: '0 0 0 1px rgba(0, 245, 255, 0.06), 0 8px 32px rgba(0, 0, 0, 0.9)',
  },
  title: {
    ...lightStyles.title,
    color: '#00d4ff',
    textShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
  },
  description: {
    ...lightStyles.description,
    color: '#ddeeff',
  },
  infoBox: {
    background: 'rgba(4, 7, 18, 0.8)',
    padding: '20px',
    borderRadius: '10px',
    border: '1px solid rgba(0, 245, 255, 0.1)',
  },
  infoTitle: {
    ...lightStyles.infoTitle,
    color: '#ff6b9d',
  },
  list: {
    ...lightStyles.list,
    color: '#8ab4cc',
  },
  horizonBox: {
    background: 'rgba(4, 7, 18, 0.8)',
    padding: '20px',
    borderRadius: '10px',
    border: '1px solid rgba(0, 245, 255, 0.12)',
    width: '120px',
  },
  horizonTitle: {
    fontSize: '1.6rem',
    color: '#00d4ff',
    marginBottom: '5px',
  },
  horizonDescription: {
    fontSize: '0.9rem',
    color: '#8ab4cc',
  },
  navButtonPrimary: {
    background: 'transparent',
    border: '1px solid rgba(0, 212, 255, 0.5)',
    color: '#00d4ff',
    boxShadow: '0 0 10px rgba(0, 212, 255, 0.2)',
  },
  navButtonDisabled: {
    background: 'transparent',
    border: '1px solid rgba(100, 100, 150, 0.25)',
    cursor: 'not-allowed',
    color: 'rgba(100, 130, 160, 0.4)',
  },
  dot: {
    ...lightStyles.dot,
    background: 'rgba(0, 245, 255, 0.15)',
  },
  dotActive: {
    background: '#00d4ff',
    boxShadow: '0 0 8px rgba(0, 212, 255, 0.5)',
  },
};

export default TutorialScreen;
